/**
 * sanitizeHtml — strict allowlist sanitizer for rich-text task titles.
 *
 * Task titles are edited with a contentEditable rich-text cell and rendered
 * with dangerouslySetInnerHTML, so every title must pass through here both
 * when it is saved and when it is rendered. The sanitizer rebuilds the
 * fragment from scratch: only allowlisted formatting tags survive, and NO
 * attributes are ever copied (which removes onerror/onclick/style/href
 * vectors entirely). Unknown-but-benign wrappers are unwrapped so their
 * text survives; script-bearing containers are dropped with their content.
 */

const ALLOWED_TAGS = new Set([
  'B', 'STRONG', 'I', 'EM', 'U', 'S',
  'BR', 'P', 'DIV', 'SPAN',
  'UL', 'OL', 'LI',
]);

/** Tags whose entire subtree is discarded (content is executable/active). */
const DROP_WITH_CONTENT = new Set([
  'SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'LINK', 'META', 'BASE',
  'TITLE', 'NOSCRIPT', 'TEMPLATE', 'SVG', 'MATH', 'FORM', 'INPUT',
  'TEXTAREA', 'SELECT', 'BUTTON', 'AUDIO', 'VIDEO', 'SOURCE', 'TRACK',
  'FRAME', 'FRAMESET', 'APPLET', 'CANVAS', 'DIALOG', 'SLOT',
]);

function sanitizeInto(source, target, doc) {
  for (const node of Array.from(source.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) {
      target.appendChild(doc.createTextNode(node.nodeValue));
      continue;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) continue; // drop comments etc.

    const tag = node.tagName.toUpperCase();
    if (DROP_WITH_CONTENT.has(tag)) continue;

    if (ALLOWED_TAGS.has(tag)) {
      // Fresh element, attributes intentionally never copied.
      const el = doc.createElement(tag.toLowerCase());
      sanitizeInto(node, el, doc);
      target.appendChild(el);
    } else {
      // Unknown wrapper (a, font, td, …): keep its text, lose the tag.
      sanitizeInto(node, target, doc);
    }
  }
}

/**
 * Sanitize an HTML string to the formatting-only allowlist.
 * Safe to call with null/undefined/plain text.
 */
export function sanitizeHtml(dirty) {
  const input = String(dirty ?? '');
  if (!input) return '';
  // Fast path: nothing that could be markup or an entity.
  if (!/[<>&]/.test(input)) return input;

  const doc = new DOMParser().parseFromString('<body></body>', 'text/html');
  const container = doc.createElement('div');
  // Parse the dirty input in an inert document (no scripts execute there).
  const parsed = new DOMParser().parseFromString(
    `<!doctype html><body>${input}`,
    'text/html',
  );
  sanitizeInto(parsed.body, container, doc);
  return container.innerHTML;
}

/** Extract readable plain text from a (possibly rich) title. */
export function htmlToText(html) {
  const input = String(html ?? '');
  if (!input) return '';
  if (!/[<>&]/.test(input)) return input;
  // Sanitize first so dropped containers (script/style/…) don't leak
  // their text content into the plain-text rendering.
  const parsed = new DOMParser().parseFromString(
    `<!doctype html><body>${sanitizeHtml(input)}`,
    'text/html',
  );
  return (parsed.body.textContent || '').trim();
}

/** Escape plain text typed into forms so it renders literally downstream. */
export function escapeHtml(text) {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
